import { useEffect, useId, useRef, useState } from "react";
import { DoorwayBrandIcon } from "./DoorwayBrandIcon";

type MarketingPage = "guide" | "blog" | "agents" | "docs" | "library";

export function MarketingNavigation({ current, onHomePage = false }: { current?: MarketingPage; onHomePage?: boolean }) {
  const [openMenu, setOpenMenu] = useState<"product" | "resources" | "mobile" | null>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const productMenuId = useId();
  const resourcesMenuId = useId();
  const mobileMenuId = useId();
  const homeHref = (hash: string) => onHomePage ? hash : `/${hash}`;

  useEffect(() => {
    const closeFromOutside = (event: PointerEvent) => {
      if (!navigationRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    const closeFromEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromEscape);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromEscape);
    };
  }, []);

  const closeMenu = () => setOpenMenu(null);

  return (
    <div className="marketing-nav-links" ref={navigationRef}>
      <div className="header-menu" data-open={openMenu === "product"}>
        <button
          className="header-menu-trigger"
          type="button"
          aria-expanded={openMenu === "product"}
          aria-controls={productMenuId}
          data-current={current === "agents" || current === "library" ? "true" : undefined}
          onClick={() => setOpenMenu(openMenu === "product" ? null : "product")}
        >
          Product <span aria-hidden="true">⌄</span>
        </button>
        <div className="header-menu-panel" id={productMenuId}>
          <a href={homeHref("#publish")} onClick={closeMenu}><strong>Publish apps</strong><small>Turn a project folder into a live link.</small></a>
          <a href="/agents" aria-current={current === "agents" ? "page" : undefined} onClick={closeMenu}><strong>Agent publishing</strong><small>Publish through Claude Code or Codex.</small></a>
          <a href="/library" aria-current={current === "library" ? "page" : undefined} onClick={closeMenu}><strong>Skills library</strong><small>Find and share reusable marketing workflows.</small></a>
          <a href={homeHref("#product")} onClick={closeMenu}><strong>Team skills</strong><small>Keep reusable workflows in sync.</small></a>
          <a href={homeHref("#security")} onClick={closeMenu}><strong>Security</strong><small>See what stays local and protected.</small></a>
        </div>
      </div>
      <div className="header-menu" data-open={openMenu === "resources"}>
        <button
          className="header-menu-trigger"
          type="button"
          aria-expanded={openMenu === "resources"}
          aria-controls={resourcesMenuId}
          data-current={current === "guide" || current === "docs" ? "true" : undefined}
          onClick={() => setOpenMenu(openMenu === "resources" ? null : "resources")}
        >
          Resources <span aria-hidden="true">⌄</span>
        </button>
        <div className="header-menu-panel" id={resourcesMenuId}>
          <a href="/how-it-works" aria-current={current === "guide" ? "page" : undefined} onClick={closeMenu}><strong>How it works</strong><small>Follow the path from folder to live link.</small></a>
          <a href="/docs/quickstart" aria-current={current === "docs" ? "page" : undefined} onClick={closeMenu}><strong>Quickstart</strong><small>Publish your first app in the browser.</small></a>
          <a href="/docs/quickstart-agents" onClick={closeMenu}><strong>Agent quickstart</strong><small>Set up Claude Code or Codex.</small></a>
        </div>
      </div>
      <a className="header-link" href="/blog" aria-current={current === "blog" ? "page" : undefined}>Blog</a>
      <a className="header-link" href={homeHref("#plans")}>Plans</a>
      <div className="header-menu mobile-header-menu" data-open={openMenu === "mobile"}>
        <button
          className="header-menu-trigger"
          type="button"
          aria-expanded={openMenu === "mobile"}
          aria-controls={mobileMenuId}
          onClick={() => setOpenMenu(openMenu === "mobile" ? null : "mobile")}
        >
          Menu <span aria-hidden="true">⌄</span>
        </button>
        <div className="header-menu-panel" id={mobileMenuId}>
          <span className="header-menu-label">Product</span>
          <a href={homeHref("#publish")} onClick={closeMenu}><strong>Publish apps</strong></a>
          <a href="/agents" aria-current={current === "agents" ? "page" : undefined} onClick={closeMenu}><strong>Agent publishing</strong></a>
          <a href="/library" aria-current={current === "library" ? "page" : undefined} onClick={closeMenu}><strong>Skills library</strong></a>
          <a href={homeHref("#product")} onClick={closeMenu}><strong>Team skills</strong></a>
          <a href={homeHref("#security")} onClick={closeMenu}><strong>Security</strong></a>
          <span className="header-menu-label">Resources</span>
          <a href="/how-it-works" aria-current={current === "guide" ? "page" : undefined} onClick={closeMenu}><strong>How it works</strong></a>
          <a href="/docs/quickstart" aria-current={current === "docs" ? "page" : undefined} onClick={closeMenu}><strong>Quickstart</strong></a>
          <a href="/docs/quickstart-agents" onClick={closeMenu}><strong>Agent quickstart</strong></a>
          <span className="header-menu-label">Explore</span>
          <a href="/blog" aria-current={current === "blog" ? "page" : undefined} onClick={closeMenu}><strong>Blog</strong></a>
          <a href={homeHref("#plans")} onClick={closeMenu}><strong>Plans</strong></a>
        </div>
      </div>
    </div>
  );
}

export function MarketingHeader({ current }: { current: MarketingPage }) {
  return (
    <header className="site-header oasis-marketing-header">
      <a className="wordmark" href="/" aria-label="Doorway home">
        <DoorwayBrandIcon className="oasis-wordmark-tile" name="logo-tile-green" />
        Doorway
      </a>
      <nav className="header-actions" aria-label="Main navigation">
        <MarketingNavigation current={current} />
        <a className="header-link oasis-marketing-sign-in" href="/manage">Sign in</a>
        <a className="primary-button header-cta" href="/manage">Publish your app <span aria-hidden="true">↓</span></a>
      </nav>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="oasis-marketing-footer">
      <a className="footer-wordmark" href="/">Doorway</a>
      <p>How marketing teams ship and share what they build with AI.</p>
      <nav className="footer-links" aria-label="Footer navigation">
        <a href="/#product">Product</a>
        <a href="/agents">Agents</a>
        <a href="/library">Skills library</a>
        <a href="/how-it-works">How it works</a>
        <a href="/docs/quickstart">Quickstart</a>
        <a href="/#plans">Plans</a>
        <a href="/blog">Blog</a>
      </nav>
    </footer>
  );
}
